/**
 * Command Palette Context
 *
 * Provides global state management for the command palette.
 * Allows any component to open/close the command palette programmatically.
 */

import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

export interface CommandPaletteContextValue {
  /** Whether the command palette is currently open */
  isOpen: boolean;

  /** Open the command palette */
  open: () => void;

  /** Close the command palette */
  close: () => void;

  /** Toggle the command palette */
  toggle: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | undefined>(undefined);

export interface CommandPaletteProviderProps {
  children: ReactNode;
}

/**
 * Command Palette Provider Component
 *
 * Wrap your app with this provider to enable command palette functionality.
 */
export function CommandPaletteProvider({ children }: CommandPaletteProviderProps) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const value: CommandPaletteContextValue = useMemo(
    () => ({
      isOpen,
      open,
      close,
      toggle,
    }),
    [isOpen, open, close, toggle]
  );

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
    </CommandPaletteContext.Provider>
  );
}

/**
 * Hook to use command palette context
 *
 * @throws Error if used outside of CommandPaletteProvider
 */
export function useCommandPalette(): CommandPaletteContextValue {
  const context = useContext(CommandPaletteContext);

  if (!context) {
    throw new Error('useCommandPalette must be used within a CommandPaletteProvider');
  }

  return context;
}
